// A checkbox dropdown for filtering a table by several values of one
// dimension. Promoted verbatim from admin's `components/multi-select.tsx`
// (the pattern is proven, zero admin-specific coupling) for reuse by a
// future BI table/filter-bar control.
//
// WHY NOT `<select multiple>`: the native control needs ctrl/cmd-click to pick a
// second value, silently drops the whole selection on a plain click, and renders as
// a tall always-open list box that would push the table off the screen. This is a
// button plus a popover of ordinary checkboxes, which is what an operator expects
// and what a screen reader reads correctly.
//
// The component is CONTROLLED and holds no filter state: the page owns the selected
// values so the four headline numbers and the table are folded from one source.
import { useCallback, useEffect, useId, useRef, useState } from "react";

import "./multi-select.css";

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps {
  /** What the dimension is, for the trigger's accessible name. */
  label: string;
  /** Trigger text when nothing is selected, e.g. "All countries". */
  allLabel: string;
  options: readonly MultiSelectOption[];
  selected: readonly string[];
  onChange: (next: readonly string[]) => void;
}

export function MultiSelect({ label, allLabel, options, selected, onChange }: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  // Close on an outside press or Escape. Both listeners are torn down with the
  // popover rather than living for the page's lifetime.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      // Escape must land focus back on the control that opened the popover,
      // otherwise a keyboard user is dropped at the top of the document.
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggle = useCallback(
    (value: string) => {
      onChange(
        selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
      );
    },
    [onChange, selected],
  );

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
        : `${selected.length} selected`;

  return (
    <div className="ms-root" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className={selected.length > 0 ? "ms-trigger ms-trigger--active" : "ms-trigger"}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ms-trigger__text">{summary}</span>
        <span aria-hidden="true" className="ms-trigger__caret">
          ▾
        </span>
      </button>

      {open ? (
        <div className="ms-panel" id={panelId} role="group" aria-label={label}>
          {options.length === 0 ? (
            <p className="ms-empty">Nothing to filter by</p>
          ) : (
            <>
              <div className="ms-options">
                {options.map((o) => (
                  <label key={o.value} className="ms-option">
                    <input
                      type="checkbox"
                      checked={selected.includes(o.value)}
                      onChange={() => toggle(o.value)}
                    />
                    <span className="ms-option__label">{o.label}</span>
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="ms-clear"
                disabled={selected.length === 0}
                onClick={() => onChange([])}
              >
                Clear
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
